/* eslint-disable no-param-reassign */
import Path from '@leverege/path';
import { Plugins } from '@leverege/plugin';
import { Blueprints } from '@leverege/api-attributes';
import { Attributes } from '@leverege/ui-attributes';
import StringUtil from '@leverege/string-util';
import I18N from '@leverege/i18n';
import Blueprint from "./Blueprint.js";
import Setters from "./dataSource/Setters.js";
import RelationshipAnalyzer from "./RelationshipAnalyzer.js";
import { AttributeCreatorFactory, getSecondaryAttributeCreators } from "./Factory.js";
const {
  t
} = I18N.ns('molten.imagine.BlueprintAnalyzer');
const SRX = /\//g;
export default class BlueprintAnalyzer {
  constructor(options) {
    const {
      blueprints,
      molten,
      networks
    } = options;
    this.options = options;
    this.molten = molten;
    this.networks = networks;
    this.lookup = new Map();
    this.blueprints = blueprints.reduce((prev, b) => {
      const mutate = this.mutateBlueprint(b, molten.getConfig('uiName'));
      const bp = new Blueprint(mutate, {
        ...options,
        blueprints: this.lookup
      });
      if (!bp.hasTag('is:hidden')) {
        this.lookup.set(bp.id, bp); // look up by id
        this.lookup.set(bp.type, bp); // look up by alias if available
        prev.push(bp);
      }
      return prev;
    }, []);
    Blueprints.set(this.blueprints);
    this.attrs = {};
    // this.childAttrs = { }
  }
  installAttributes() {
    // We could use a for loop for a little more performance, but making 
    // this once is a big win and keeps readibility
    const addAttrPlugin = attr => {
      this.molten.addPlugin('Attribute', attr);
    };
    const len = this.blueprints.length;
    for (let n = 0; n < len; n++) {
      const b = this.blueprints[n];
      const attrs = this.getAttrsFor(b);
      attrs.forEach(addAttrPlugin);

      // Depends on previous block of code. Extracted for legibility.
      const metaAttrs = this.getMetaAttrsFor(b);
      metaAttrs.forEach(addAttrPlugin);
      const histAttrs = this.getHistoryAttrs(b);
      histAttrs.forEach(addAttrPlugin);
      const namedHeaderAttributes = this.getNamedHeaderAttributes(b);
      namedHeaderAttributes.forEach(addAttrPlugin);
      for (let an = 0; an < attrs.length; an++) {
        const attr = attrs[an];
        const sAttrs = getSecondaryAttributeCreators(attr.source?.attribute);
        for (let sn = 0; sn < sAttrs.length; sn++) {
          const a = sAttrs[sn];
          const creator = AttributeCreatorFactory.get(a);
          if (creator) {
            creator(a, attr, {
              blueprint: b,
              molten: this.molten
            });
          }
        }
      }
    }
  }
  getHistoryAttrs(blueprint) {
    const attrs = this.getAttrsFor(blueprint, 'history');
    const opts = {
      filterable: true,
      sortable: true
    };
    const timeSource = {
      type: 'imagine.Blueprint',
      field: '$time',
      fieldType: 'timestamp'
    };
    const idSource = {
      type: 'imagine.Blueprint',
      field: '$id',
      fieldType: 'keyword'
    };
    attrs.push({
      id: `${blueprint.type}.history.time`,
      name: `${blueprint.type}.history.time`,
      get displayName() {
        return t('time');
      },
      valueType: 'timestamp',
      objectType: `${blueprint.type}.history`,
      layout: {
        group: []
      },
      get: obj => {
        const time = obj && obj.time;
        return time == null ? null : new Date(time);
      },
      getOption: optionName => {
        return opts[optionName];
      },
      blueprint: timeSource,
      source: timeSource
    }, {
      id: `${blueprint.type}.history.id`,
      name: `${blueprint.type}.history.id`,
      get displayName() {
        return t('recordId');
      },
      valueType: 'string',
      objectType: `${blueprint.type}.history`,
      layout: {
        group: []
      },
      get: obj => obj && obj.id,
      getOption: optionName => {
        return opts[optionName];
      },
      blueprint: idSource,
      source: idSource
    });
    return attrs;
  }

  /**
   * Returns the ui-attributes Attribute objects for the
   * blueprint id. This will build them if they have not
   * been made
   */
  getAttrsFor(blueprint, subtype) {
    const subId = subtype ? `.${subtype}` : '';
    if (this.attrs[`${blueprint.id}${subId}`] == null) {
      const arr = [];
      this.attrs[`${blueprint.id}${subId}`] = arr;
      const {
        local,
        child,
        parent
      } = blueprint.analysis.attributes;
      const bNamedAttr = blueprint.analysis.namedAttributes;
      const {
        name,
        possName
      } = bNamedAttr;
      const networkTags = blueprint.getTagsStartingWith('network:', true);
      const hasNetworkNameTag = networkTags.some(tag => tag.endsWith('isName'));
      this.toDeviceAttributes(blueprint, name == null && possName == null && hasNetworkNameTag === false, subtype).forEach(att => {
        arr.push(att);
      });
      let hasGeoPosition = bNamedAttr.geoPosition != null;
      Object.entries(bNamedAttr).forEach(([key, value]) => {
        const na = this.toAttribute(blueprint, value, key, subtype);
        if (na) {
          arr.push(na);
        }
      });
      local.forEach(attr => {
        const att = this.toAttribute(blueprint, attr, null, subtype);
        if (att) {
          arr.push(att);
        }
      });
      parent.forEach(attr => {
        const att = this.toAttribute(blueprint, attr, null, subtype);
        const meta = this.toRelationshipMetaAttributes(attr, subtype);
        if (att) {
          arr.push(att);
        }
        arr.push(...meta);
      });
      child.forEach(attr => {
        const {
          targetBlueprintId
        } = attr;
        const uiAttrs = this.getAttrsFor(this.lookup.get(targetBlueprintId), subtype);
        uiAttrs.forEach(uiAttr => {
          const att = this.toForwardingAttribute(blueprint, attr, uiAttr, subtype);
          if (att) {
            if (!hasGeoPosition && uiAttr.name === 'geoPosition') {
              hasGeoPosition = true;
              const fgp = {
                ...att,
                name: 'geoPosition',
                displayName: t('position')
              };
              arr.push(fgp);
            } else if (attr.name === 'name') {
              // no op
            } else {
              const nameTag = uiAttr?.blueprint?.attribute?.getTagStartingWith('forwardAttribute:', true);
              arr.push(att);
              if (nameTag != null) {
                arr.push({
                  ...att,
                  name: nameTag,
                  id: `${att.id}:${nameTag}`
                });
              }
            }
          }
        });
        const meta = this.toRelationshipMetaAttributes(attr, subtype);
        arr.push(...meta);
      });
    }
    return this.attrs[`${blueprint.id}${subId}`];
  }
  getMetaAttrsFor(blueprint) {
    if (this.attrs[`${blueprint.id}:meta`] == null) {
      const arr = [];
      this.attrs[`${blueprint.id}:meta`] = arr;
      const {
        local,
        child,
        parent
      } = blueprint.analysis.attributes;
      parent.forEach(attr => {
        const att = this.toMetaAttribute(blueprint, attr, 'updated', 'timestamp');
        arr.push(att);
      });
      local.forEach(attr => {
        const att = this.toMetaAttribute(blueprint, attr, 'updated', 'timestamp');
        arr.push(att);
      });
      child.forEach(attr => {
        const {
          targetBlueprintId
        } = attr;
        const uiAttrs = this.getMetaAttrsFor(this.lookup.get(targetBlueprintId));
        uiAttrs.forEach(uiAttr => {
          const att = this.toForwardingMetaAttribute(blueprint, attr, uiAttr);
          arr.push(att);
        });
      });
    }
    return this.attrs[`${blueprint.id}:meta`];
  }
  installDataSources() {
    // Add createDataSources
    this.blueprints.forEach(b => {
      Plugins.add('DataSource', {
        type: b.type,
        dataSource: b.getDataSource()
      });
      Plugins.add('DataSource', {
        type: `${b.type}.history`,
        dataSource: b.getHistoryDataSource()
      });
    });
  }
  async install() {
    this.installDataSources();
    this.installAttributes();
    await RelationshipAnalyzer.run(this.molten, this.blueprints);
  }
  toRelationshipMetaAttributes(attr, subtype) {
    const subId = subtype != null ? `.${subtype}` : '';
    const {
      blueprint,
      path
    } = attr;
    const timePath = `data/${path}/time`;
    const timeAccessor = Path(timePath);
    const group = path.split('/').map(item => StringUtil.camelCaseToTitleCase(item));
    group[0] = attr.name;
    const opts = {
      filterable: true,
      sortable: true
    };
    const source = {
      type: 'imagine.Blueprint',
      field: timePath,
      fieldType: 'timestamp'
    };
    return [{
      id: `${blueprint.type}.${attr.path}${subId}.relationshipCreationTime`,
      name: `${blueprint.type}.${attr.path}${subId}.relationshipCreationTime`,
      get displayName() {
        return t('relationshipCreationTime', {
          blueprintName: blueprint.name,
          attrName: attr.name
        });
      },
      valueType: 'timestamp',
      objectType: `${blueprint.type}${subId}`,
      layout: {
        group
      },
      get: obj => {
        const d = timeAccessor.get(obj);
        return d == null ? null : new Date(d);
      },
      getOption: key => {
        return opts[key];
      },
      blueprint: source,
      source
    }];
  }
  toDeviceAttributes(blueprint, addName, subtype) {
    const subId = subtype != null ? `.${subtype}` : '';
    const deviceIdGet = obj => {
      if (!subId) {
        return obj && obj.id;
      }
      return obj && obj.deviceId;
    };
    const nameSource = subtype ? undefined : {
      // TODO: Not sure if this is a great way to handle history & events
      type: 'imagine.Blueprint',
      field: 'name.keyword',
      updatePath: 'header/name',
      editable: blueprint.hasTag('formEditDeviceName'),
      fieldType: 'string'
    };
    const idSource = {
      type: 'imagine.Blueprint',
      field: 'id',
      fieldType: 'keyword'
    };
    const createdSource = {
      type: 'imagine.Blueprint',
      field: 'created',
      fieldType: 'timestamp'
    };
    const rtn = [{
      id: `${blueprint.type}${subId}.deviceName`,
      name: `${blueprint.type}${subId}.deviceName`,
      get displayName() {
        return t('deviceName');
      },
      valueType: 'string',
      objectType: `${blueprint.type}${subId}`,
      layout: {
        group: ['Device']
      },
      get: obj => obj && obj.name,
      getOption: key => {
        if (key === 'filterable') {
          return blueprint.hasTag('deviceName:filterable');
        }
        if (key === 'sortable') {
          return blueprint.hasTag('deviceName:sortable');
        }
        return null;
      },
      blueprint: nameSource,
      source: nameSource,
      set: Setters.createHeaderSet('name', blueprint),
      canSet: Setters.createHeaderCanSet('name', blueprint)
    }, {
      id: `${blueprint.type}${subId}.deviceId`,
      name: `${blueprint.type}${subId}.deviceId`,
      get displayName() {
        return t('deviceId');
      },
      valueType: 'string',
      objectType: `${blueprint.type}${subId}`,
      layout: {
        group: ['Device']
      },
      get: deviceIdGet,
      getOption: key => {
        if (key === 'filterable') {
          return blueprint.hasTag('deviceId:filterable');
        }
        if (key === 'sortable') {
          return blueprint.hasTag('deviceId:sortable');
        }
        return null;
      },
      blueprint: idSource,
      source: idSource
    }, {
      id: `${blueprint.type}${subId}.icon`,
      name: 'icon',
      get displayName() {
        return t('icon');
      },
      valueType: 'icon',
      objectType: `${blueprint.type}${subId}`,
      layout: {},
      get: () => {
        return blueprint.getIcon('typeIcon') || blueprint.getIcon('navIcon') || blueprint.getIcon('screenIcon');
      }
    }, ...(subId !== '.history' ? [{
      id: `${blueprint.type}${subId}.created`,
      name: `${blueprint.type}${subId}.created`,
      get displayName() {
        return t('created');
      },
      valueType: 'timestamp',
      objectType: `${blueprint.type}${subId}`,
      layout: {},
      getOption: key => {
        if (key === 'filterable') {
          return true;
        }
        if (key === 'sortable') {
          return true;
        }
        return null;
      },
      blueprint: createdSource,
      source: createdSource,
      filterable: true,
      sortable: true,
      get: obj => obj && obj.created
    }] : [])];
    if (addName) {
      rtn.push({
        ...rtn[0],
        id: `${blueprint.type}${subId}.name`,
        layout: {},
        name: 'name',
        get displayName() {
          return t('name');
        }
      });
    }
    const networks = this.networks || [];
    networks.forEach(net => {
      const tag = `network:${net.id}`;
      if (blueprint.tags?.includes(tag)) {
        const keys = net.aliasKeys?.split(',') || [];
        keys.forEach(key => {
          const nameKey = `network:${net.id}:${key}:isName`;
          const opts = {
            filterable: blueprint.hasTag(`${tag}:filterable`),
            sortable: blueprint.hasTag(`${tag}:sortable`)
          };
          const source = {
            type: 'imagine.Blueprint',
            updatePath: `header/networkAliases/${net.id}/${key}`,
            editable: blueprint.tags?.includes(`networkEditable:${net.id}:${key}`),
            isExternalId: true,
            field: `networkAliases/${net.id}/${key}`,
            fieldType: 'keyword'
          };
          const netAttr = {
            name: `${blueprint.type}.${net.id}.${key}`,
            displayName: StringUtil.camelCaseToTitleCase(`${key}`),
            valueType: 'string',
            objectType: `${blueprint.type}${subId}`,
            layout: {
              group: ['Network', net.name]
            },
            get: obj => obj?.networkAliases?.[net.id]?.[key] || null,
            getOption: key => {
              return opts[key];
            },
            blueprint: source,
            source
          };
          if (source.editable) {
            netAttr.canSet = Setters.createHeaderCanSet(netAttr.name);
            netAttr.set = Setters.createNetworkIdSet(source.field);
          }
          rtn.push(netAttr);
          if (blueprint.tags?.includes(nameKey)) {
            rtn.push({
              ...netAttr,
              name: 'name'
            });
          }
        });
      }
    });
    return rtn;
  }
  getNamedHeaderAttributes(blueprint) {
    if (this.attrs[`${blueprint.id}.namedHeaderAttrs`] == null) {
      const arr = [];
      this.attrs[`${blueprint.id}.namedHeaderAttrs`] = arr;
      const headerAttributeTags = blueprint.getTagsStartingWith('headerAttribute:', true);
      headerAttributeTags.forEach(headerAttributeTag => {
        const parts = headerAttributeTag?.split(':');
        if (parts.length === 2) {
          const [header, attrName] = parts;
          const attribute = Attributes.getAttribute(`${blueprint.type}.${header}`, blueprint.type);
          if (attribute) {
            arr.push({
              ...attribute,
              name: attrName,
              id: `${attribute.id}:${attrName}`
            });
          }
        } else {
          // eslint-disable-next-line no-console
          console.warn(`headerAttributeTag ${headerAttributeTag} specified in wrong format for ${blueprint.type}`);
        }
      });
    }
    return this.attrs[`${blueprint.id}.namedHeaderAttrs`];
  }
  getValueType(attr) {
    const {
      type,
      path
    } = attr;
    let {
      type: rType
    } = attr;
    if (type === 'string' && (attr.hasTag('is:image') || attr.hasTag('is:imageURL'))) {
      // no support for image*
      return 'image';
    }
    if (type === 'string' && (attr.hasTag('is:resourceImage') || attr.hasTag('is:imageUpload'))) {
      rType = 'resourceImage';
    }
    if (path?.endsWith('@')) {
      return `${rType}@`;
    }
    if (path?.endsWith('*')) {
      return `${rType}*`;
    }
    return rType;
  }
  toAttribute(blueprint, attr, rename, subtype) {
    const {
      path
    } = attr;
    const isCollection = path.endsWith('*') || path.endsWith('@');
    const group = path.split('/').map(item => StringUtil.camelCaseToTitleCase(item));
    group.pop();
    const subId = subtype != null ? `.${subtype}` : '';
    // If this is a parent relationship type, have the getter return the parentId

    const fieldPath = attr.isParent() ? `data/${path}/id` : attr.isOneToOne() ? `data/${path}/id` : isCollection ? `data/${path.slice(0, -1)}` : `data/${path}`;
    const editable = attr?.model?.source === 'user';
    const opts = {
      filterable: attr.isFilterable && !isCollection,
      sortable: attr.isSortable && !isCollection,
      i18nGetter: (...args) => attr.getString(...args)
    };
    const source = {
      type: 'imagine.Blueprint',
      field: fieldPath,
      updatePath: fieldPath,
      editable,
      attribute: attr,
      renamed: rename != null,
      forwarded: attr.isOneToOne(),
      fieldType: attr.searchType
    };
    const valueType = this.getValueType(attr);
    if (subtype === 'history' && valueType === 'resourceImage') {
      return null;
    }
    const rtn = {
      id: `${blueprint.type}${subId}.${rename ? `${path}/${rename}` : path}`,
      name: rename || `${blueprint.type}${subId}.${path.replace(SRX, '.')}`,
      displayName: StringUtil.camelCaseToTitleCase(isCollection ? attr.name.replaceAll(/\*|@/g, '') : attr.name),
      valueType,
      objectType: `${blueprint.type}${subId}`,
      layout: {
        group
      },
      // ApiAttribute data live under data.
      get: this.createGetter(attr, fieldPath),
      getOption: key => {
        return opts[key];
      },
      blueprint: source,
      source
    };
    if (attr.model.base) {
      rtn.baseUnit = attr.model.base;
    }
    if (editable) {
      // Make the setter
      rtn.set = attr.isParent() ? Setters.createParentAttrSet(attr) : attr.isOneToOne() ? Setters.createRelationshipAttrSet(attr) : valueType === 'resourceImage' ? Setters.createResourceImageSet(attr) : valueType === 'resourceImage@' ? Setters.createResourceImageListSet(attr) : Setters.createAttrSet(attr);
      rtn.canSet = attr.isRelationship() ? Setters.createRelationshipAttrCanSet(attr) : attr.isParent() ? Setters.createParentAttrCanSet(attr) : valueType === 'resourceImage' ? Setters.createResourceImageCanSet(attr) : valueType === 'resourceImage@' ? Setters.createResourceImageListCanSet(attr) : Setters.createAttrCanSet(attr);
    }
    return rtn;
  }
  toMetaAttribute(blueprint, attr, metaPath, metaType) {
    const {
      path
    } = attr;
    const group = path.split('/').map(item => StringUtil.camelCaseToTitleCase(item));
    group.pop();
    group.push(t('meta'));
    const opts = {
      filterable: true,
      sortable: true
    };
    const source = {
      type: 'imagine.Blueprint',
      field: `dataMetadata/${path}/${metaPath}`,
      attribute: attr,
      meta: true
    };
    const id = `${blueprint.type}.${path}:${metaPath}`;
    const rtn = {
      id,
      name: id,
      get displayName() {
        return `${StringUtil.camelCaseToTitleCase(attr.name)} ${StringUtil.capitalize(t(metaPath))}`;
      },
      valueType: metaType,
      objectType: blueprint.type,
      layout: {
        group,
        sort: 'zzz'
      },
      get: obj => {
        return Path.get(source.field, obj);
      },
      getOption: key => {
        return opts[key];
      },
      blueprint: source,
      source
    };
    return rtn;
  }

  /**
   * Create an attribute that will look up data from another
   * attribute
   * @param {Blueprint} blueprint
   * @param {object} attr the api-attribute attribute
   * @param {object} uiAttr the ui-attribute attribute
   */
  toForwardingAttribute(blueprint, attr, uiAttr, subtype) {
    const {
      path,
      name
    } = attr;
    const group = [path.split('/').map(item => item ? StringUtil.camelCaseToTitleCase(item) : item), ...(uiAttr.layout.group || [])];
    group[0] = name;
    const forward = this.createGetter(attr, `data/${path}`);
    const forwardName = uiAttr.name;
    const forwardObjectType = uiAttr.objectType;
    const subId = subtype != null ? `.${subtype}` : '';
    const opts = {
      filterable: uiAttr?.blueprint?.attribute?.isFilterable,
      sortable: uiAttr?.blueprint?.attribute?.isSortable,
      i18nGetter: (...args) => attr.getString(...args)
    };
    const source = {
      type: 'imagine.Blueprint',
      field: uiAttr?.blueprint?.field ? `data/${attr.path}/${uiAttr.blueprint.field}` : undefined,
      attribute: uiAttr?.blueprint?.attribute,
      forwarded: true,
      fieldType: uiAttr?.fieldType
    };
    const id = `${blueprint.type}.${path.replace(SRX, '.')}${subId}$${forwardName}`;
    const rtn = {
      id,
      name: id,
      get displayName() {
        return t('forwardingAttribute', {
          name,
          attrName: uiAttr.displayName
        });
      },
      valueType: uiAttr.valueType,
      objectType: `${blueprint.type}${subId}`,
      layout: {
        group
      },
      get: (obj, cxt) => {
        const data = forward(obj, cxt);
        if (data == null) {
          return null;
        }
        return Attributes.get(forwardName, forwardObjectType, data, cxt);
      },
      getOption: key => {
        return opts[key];
      },
      blueprint: source,
      source
    };
    if (uiAttr.baseUnit) {
      rtn.baseUnit = uiAttr.baseUnit;
    }
    return rtn;
  }
  toForwardingMetaAttribute(blueprint, attr, uiAttr) {
    const {
      path,
      name
    } = attr;
    const group = [path.split('/').map(item => item ? StringUtil.camelCaseToTitleCase(item) : item), ...(uiAttr.layout.group || [])];
    group[0] = name;
    const opts = {
      filterable: true,
      sortable: true
    };
    const source = {
      type: 'imagine.Blueprint',
      field: `dataMetadata/${attr.path}/data/${uiAttr.blueprint.field.replace('dataMetadata', '')}`,
      attribute: uiAttr?.blueprint?.attribute,
      meta: true
    };
    const id = `${blueprint.type}.${path.replace(SRX, '.')}$${uiAttr.name}`;
    return {
      id,
      name: id,
      get displayName() {
        return t('forwardingAttribute', {
          name,
          attrName: uiAttr.displayName
        });
      },
      valueType: uiAttr.valueType,
      objectType: blueprint.type,
      layout: {
        group
      },
      get: (obj, cxt) => {
        return Path.get(source.field, obj);
      },
      getOption: key => {
        return opts[key];
      },
      blueprint: source,
      source
    };
  }
  createGetter(attr, path) {
    let accessor;
    const adjustedPath = path.endsWith('*') || path.endsWith('@') ? path.slice(0, -1) : path;
    if (adjustedPath.includes('/')) {
      const p = Path(adjustedPath);
      accessor = obj => {
        return p.get(obj);
      };
    } else {
      // eslint-disable-next-line react/destructuring-assignment
      accessor = function (obj) {
        return obj == null ? null : obj[adjustedPath];
      };
    }
    if (attr.type === 'timestamp') {
      return obj => {
        const d = accessor(obj);
        return d == null ? null : new Date(d);
      };
    }

    // hacky way to look for unit - use api-attributes
    if (attr?.model?.base != null) {
      return obj => {
        const d = accessor(obj);
        return d == null ? null : {
          type: attr.model.type,
          value: d,
          unit: attr.model.base
        };
      };
    }
    return obj => {
      return accessor(obj);
    };
  }
  mutateBlueprint(bp, mutateTo) {
    if (mutateTo == null) {
      return bp;
    }
    const nbp = this.mutate({
      ...bp
    }, mutateTo);
    if (Array.isArray(nbp.attributes)) {
      nbp.attributes = nbp.attributes.map(attr => {
        return this.mutate(attr, mutateTo);
      }).filter(attr => attr != null);
      this.mutate();
    }
    return nbp;
  }
  mutate(bp, mutateTo) {
    const mutations = bp?.metadata?.mutations;
    // Mutation per persona will not work. Only by ui
    if (mutateTo == null || mutations == null || mutations[mutateTo] == null) {
      return bp;
    }
    // Replace tags and selectively paths in the metadata
    const {
      tags,
      metadata
    } = mutations[mutateTo];
    if (tags == null && metadata == null) {
      return bp;
    }
    const nbp = {
      ...bp
    };
    if (tags != null) {
      if (Array.isArray(tags)) {
        nbp.tags = tags;
      } else if (typeof tags === 'string') {
        nbp.tags = tags.split(/[, ]+/).filter(Boolean);
      } else {
        nbp.tags = [];
      }
    }
    if (metadata === '') {
      nbp.metadata = {};
    } else if (Array.isArray(metadata)) {
      // All patch like replacement
      for (let n = 0; n < metadata.length; n++) {
        const {
          path,
          value
        } = metadata[n];
        nbp.metadata = Path.copy(path, nbp.metadata, value);
      }
    } else if (metadata && typeof metadata === 'object') {
      nbp.metadata = metadata;
    }
    return nbp;
  }
}